import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO, isAfter, isBefore } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function DisplayKiosk() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const now = useClock();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/display/${code}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Display ikke fundet");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // refresh every 20s
    return () => clearInterval(t);
  }, [code]);

  // Auto-fullscreen attempt on first click
  useEffect(() => {
    const handler = () => {
      const el = document.documentElement;
      if (el.requestFullscreen && !document.fullscreenElement) {
        el.requestFullscreen().catch(() => {});
      }
    };
    document.addEventListener("click", handler, { once: true });
    return () => document.removeEventListener("click", handler);
  }, []);

  if (error) {
    return (
      <div className="kiosk-fullscreen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-5xl font-bold mb-4">Display ikke fundet</h1>
          <p className="text-slate-400 text-xl">Kode: {code}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="kiosk-fullscreen flex items-center justify-center">
        <div className="text-slate-400 text-2xl font-display">Indlæser…</div>
      </div>
    );
  }

  const { rooms, bookings, display } = data;

  return (
    <div className="kiosk-fullscreen p-8 md:p-12 flex flex-col" data-testid="display-kiosk">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-slate-400 uppercase tracking-widest text-sm font-medium">
            {format(now, "EEEE", { locale: da })}
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold mt-1">
            {format(now, "d. MMMM yyyy", { locale: da })}
          </h2>
          <p className="text-slate-500 text-sm mt-2">{display.name}</p>
        </div>
        <div className="text-right">
          <div className="font-display text-7xl md:text-9xl font-bold tabular-nums leading-none" data-testid="display-clock">
            {format(now, "HH:mm")}
          </div>
          <div className="flex items-center justify-end gap-2 mt-3 text-sm text-slate-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400 live-pulse" />
            LIVE
          </div>
        </div>
      </div>

      {/* Rooms grid */}
      <div className={`flex-1 grid gap-5 ${gridCols(rooms.length)}`}>
        {rooms.map((room) => (
          <RoomTile
            key={room.id}
            room={room}
            bookings={bookings.filter((b) => b.room_id === room.id)}
            now={now}
            viewMode={display.view_mode}
          />
        ))}
      </div>
    </div>
  );
}

function gridCols(n) {
  if (n <= 1) return "grid-cols-1";
  if (n <= 2) return "grid-cols-1 md:grid-cols-2";
  if (n <= 4) return "grid-cols-2";
  if (n <= 6) return "grid-cols-2 md:grid-cols-3";
  return "grid-cols-2 md:grid-cols-4";
}

function RoomTile({ room, bookings, now, viewMode }) {
  const sorted = useMemo(
    () => [...bookings].filter((b) => b.status !== "cancelled").sort((a, b) => a.start.localeCompare(b.start)),
    [bookings],
  );
  const current = sorted.find((b) => {
    const s = parseISO(b.start);
    const e = parseISO(b.end);
    return !isAfter(s, now) && isAfter(e, now);
  });
  const upcoming = sorted.filter((b) => isAfter(parseISO(b.start), now));
  const next = upcoming[0];

  const isOccupied = Boolean(current);
  const statusBg = isOccupied
    ? "bg-gradient-to-br from-red-500/20 to-red-500/5 border-red-500/40"
    : "bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/40";
  const statusDot = isOccupied ? "bg-red-400" : "bg-emerald-400";
  const statusText = isOccupied ? "OPTAGET" : "LEDIG";
  const statusColor = isOccupied ? "text-red-300" : "text-emerald-300";

  return (
    <div className={`rounded-3xl border ${statusBg} p-6 md:p-8 flex flex-col backdrop-blur-sm`} data-testid={`room-tile-${room.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-3 h-12 rounded-full" style={{ background: room.color }} />
          <div className="min-w-0">
            <h3 className="font-display text-2xl md:text-4xl font-bold truncate">{room.name}</h3>
            <p className="text-slate-400 text-sm md:text-base mt-1">
              {room.floor || ""} · Op til {room.capacity} pers.
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-2 justify-end">
            <span className={`w-2.5 h-2.5 rounded-full ${statusDot} live-pulse`} />
            <span className={`font-display text-lg md:text-2xl font-bold tracking-wide ${statusColor}`} data-testid={`status-${room.id}`}>
              {statusText}
            </span>
          </div>
        </div>
      </div>

      {viewMode === "now_next" ? (
        <div className="flex-1 flex flex-col gap-4">
          {current ? (
            <div className="bg-white/5 rounded-2xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Nu</p>
              <p className="font-display text-2xl md:text-3xl font-semibold leading-tight">{current.title}</p>
              <p className="text-slate-300 mt-2 text-base md:text-xl tabular-nums">
                {format(parseISO(current.start), "HH:mm")} – {format(parseISO(current.end), "HH:mm")}
              </p>
            </div>
          ) : next ? (
            <div className="bg-white/5 rounded-2xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Næste</p>
              <p className="font-display text-2xl md:text-3xl font-semibold leading-tight">{next.title}</p>
              <p className="text-slate-300 mt-2 text-base md:text-xl tabular-nums">
                Fra {format(parseISO(next.start), "HH:mm")} – {format(parseISO(next.end), "HH:mm")}
              </p>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-slate-400 font-display text-xl md:text-2xl">Ingen bookinger i dag</p>
            </div>
          )}
          {current && next && (
            <div className="bg-white/5 rounded-2xl p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Næste</p>
              <p className="font-medium text-lg">{next.title}</p>
              <p className="text-slate-400 text-sm tabular-nums">
                {format(parseISO(next.start), "HH:mm")} – {format(parseISO(next.end), "HH:mm")}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 space-y-2 overflow-hidden">
          <p className="text-xs text-slate-400 uppercase tracking-wider">{viewMode === "week" ? "Denne uge" : "I dag"}</p>
          {sorted.length === 0 && <p className="text-slate-500 font-display text-lg">Ingen bookinger</p>}
          {sorted.slice(0, viewMode === "week" ? 8 : 6).map((b) => {
            const isCur = b === current;
            const isPast = isBefore(parseISO(b.end), now);
            return (
              <div
                key={b.id}
                className={`flex items-center justify-between rounded-xl px-4 py-3 ${
                  isCur ? "bg-red-500/20 border border-red-500/40" : isPast ? "bg-white/3 opacity-50" : "bg-white/5"
                }`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{b.title}</p>
                  {viewMode === "week" && (
                    <p className="text-xs text-slate-400">
                      {format(parseISO(b.start), "EEE d/M", { locale: da })}
                    </p>
                  )}
                </div>
                <p className="text-slate-300 tabular-nums text-sm md:text-base shrink-0 ml-3">
                  {format(parseISO(b.start), "HH:mm")}–{format(parseISO(b.end), "HH:mm")}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
